import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Input, InputNumber, message, Spin } from "antd";
import {
  StyledAntdModal,
  StyledAntdTable,
  StyledButton
} from "common/components/StyledElements";
import APIConstants from "common/constants/APIConstants";
import { useAuthContext } from "common/contexts/AuthContext";
import backArrow from "common/icons/backArrow.svg";
import { api } from "common/utils/APIMethods";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import SelectProductsModal, {
  selectProductsModalProps
} from "./ProductMappingModal";
import ProductPreBatchTable from "./ProductPreBatchTable";

const Container = styled.div`
  padding: 20px;
`;

const DetailsCard = styled.div`
  margin-top: 20px;
  border: 1px solid #e8e9ea;
  border-radius: 10px;
  background: #fff;
  padding: 16px;
`;

const AssignedInventoryCard = styled(DetailsCard)``;

const CardTitle = styled.h3`
  margin: 0 0 12px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px 16px;
`;

const FieldItem = styled.div`
  font-size: 14px;
`;

const Arrow = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const SectionToggle = styled.button`
  flex: 1;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-align: left;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackImage = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
`;

const renderAssignedInventoryExpandedRow = (inventory) => (
  <FieldsGrid>
    <FieldItem>
      <strong>Packing:</strong> {inventory.packing || "-"}
    </FieldItem>
    <FieldItem>
      <strong>Depot:</strong> {inventory.location?.depot || "-"}
    </FieldItem>
  </FieldsGrid>
);

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authConfig } = useAuthContext();

  const { getProductById, createProductionRequirement, getSettingsByUserId } =
    APIConstants;

  const {
    data: product,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["product", id],
    queryFn: () => api({ url: `${getProductById}/${id}` }, authConfig)
  });

  const { data } = useQuery({
    queryKey: ["settings"],
    queryFn: () =>
      api(
        {
          url: getSettingsByUserId
        },
        authConfig
      )
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInventoryModalVisible, setIsInventoryModalVisible] = useState(false);
  const [isAssignedInventoryOpen, setIsAssignedInventoryOpen] = useState(false);
  const [isPreBatchesOpen, setIsPreBatchesOpen] = useState(false);
  const [productQuantity, setProductQuantity] = useState(null);
  const [productUOM, setProductUOM] = useState("");
  const [materialQuantities, setMaterialQuantities] = useState({});

  const resetModalState = () => {
    setIsModalOpen(false);
    setProductQuantity(null);
    setProductUOM("");
    setMaterialQuantities({});
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (body) =>
      api(
        {
          url: createProductionRequirement,
          method: "POST",
          body
        },
        authConfig
      ),
    onSuccess: async () => {
      message.success("Production requirement saved successfully");
      resetModalState();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["preBatches"],
          refetchType: "all"
        }),
        queryClient.invalidateQueries({
          queryKey: ["product", id],
          refetchType: "all"
        })
      ]);
    },
    onError: () => message.error("Failed to save requirement")
  });

  if (isLoading) {
    return (
      <Container>
        <Spin size="large" />
      </Container>
    );
  }

  if (isError || !product) {
    return (
      <Container>
        <BackImage src={backArrow} onClick={() => navigate(-1)} alt="back" />
        <div style={{ marginTop: 20 }}>Product not found</div>
      </Container>
    );
  }

  const handleSubmit = () => {
    if (!productQuantity || productQuantity <= 0) {
      message.error("Enter valid product quantity");
      return;
    }
    if (!productUOM?.trim()) {
      message.error("Enter product UOM");
      return;
    }

    const materials = (product.inventories || [])
      .filter((inv) => materialQuantities[inv.id] > 0)
      .map((inv) => ({
        inventoryId: inv.id,
        inventoryQuantity: materialQuantities[inv.id],
        inventoryUOM: inv.unitOfMeasurement
      }));

    if (materials.length === 0) {
      message.error("Enter at least one material quantity");
      return;
    }

    mutate({
      productId: product.id,
      productQuantity,
      productUOM: productUOM.trim(),
      materials
    });
  };

  const columns = [
    {
      title: "Material Name",
      dataIndex: "materialName"
    },
    {
      title: "UOM",
      dataIndex: "unitOfMeasurement"
    },
    {
      title: "Type",
      dataIndex: "type"
    },
    {
      title: "Quantity",
      render: (_, record) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          placeholder="Enter Qty"
          value={materialQuantities[record.id] || null}
          onChange={(value) =>
            setMaterialQuantities((prev) => ({
              ...prev,
              [record.id]: value
            }))
          }
        />
      )
    }
  ];

  const assignedInventories = product.inventories || [];
  const assignedInventoryColumns = [
    {
      title: "Material",
      dataIndex: "materialName"
    },
    {
      title: "Code",
      dataIndex: "materialCode"
    },
    {
      title: "Type",
      dataIndex: "type"
    },
    {
      title: "UOM",
      dataIndex: "unitOfMeasurement"
    }
  ];

  return (
    <Container>
      <BackImage src={backArrow} onClick={() => navigate(-1)} alt="back" />

      <DetailsCard>
        <CardTitle>{product.productName}</CardTitle>
        <FieldsGrid>
          <FieldItem>
            <strong>Product Code:</strong> {product.productCode}
          </FieldItem>
          <FieldItem>
            <strong>Division:</strong> {product.division || "-"}
          </FieldItem>
          <FieldItem>
            <strong>Depot:</strong> {product.location?.depot || "-"}
          </FieldItem>
          <FieldItem>
            <strong>Assigned Inventories:</strong> {assignedInventories.length}
          </FieldItem>
        </FieldsGrid>
      </DetailsCard>

      <AssignedInventoryCard>
        <SectionHeader>
          <SectionToggle
            type="button"
            onClick={() => setIsAssignedInventoryOpen((prev) => !prev)}
          >
            <Arrow>{isAssignedInventoryOpen ? "v" : ">"}</Arrow>
            <CardTitle style={{ margin: 0 }}>Assigned Inventory</CardTitle>
          </SectionToggle>

          <ActionsContainer>
            <StyledButton
              type="primary"
              onClick={() => setIsInventoryModalVisible(true)}
            >
              Assign Inventory
            </StyledButton>
          </ActionsContainer>
        </SectionHeader>

        {isAssignedInventoryOpen && (
          <div style={{ marginTop: 12 }}>
            {assignedInventories.length === 0 ? (
              <div>No inventories assigned yet.</div>
            ) : (
              <StyledAntdTable
                size="small"
                rowKey="id"
                columns={assignedInventoryColumns}
                dataSource={assignedInventories}
                pagination={false}
                expandable={{
                  expandedRowRender: renderAssignedInventoryExpandedRow
                }}
              />
            )}
          </div>
        )}
      </AssignedInventoryCard>
      {data?.enablePreBatchOrders && (
        <DetailsCard>
          <SectionHeader>
            <SectionToggle
              type="button"
              onClick={() => setIsPreBatchesOpen((prev) => !prev)}
            >
              <Arrow>{isPreBatchesOpen ? "v" : ">"}</Arrow>
              <CardTitle style={{ margin: 0 }}>Pre Batches</CardTitle>
            </SectionToggle>

            <ActionsContainer>
              <StyledButton type="primary" onClick={() => setIsModalOpen(true)}>
                Add Batch
              </StyledButton>
            </ActionsContainer>
          </SectionHeader>

          {isPreBatchesOpen && (
            <div style={{ marginTop: 12 }}>
              <ProductPreBatchTable productId={product.id} />
            </div>
          )}
        </DetailsCard>
      )}

      <StyledAntdModal
        open={isModalOpen}
        onCancel={resetModalState}
        footer={null}
        width={850}
        centered
        destroyOnHidden
        title={`Create Batch - ${product.productName}`}
      >
        <Form layout="vertical" style={{ marginBottom: 20 }}>
          <Form.Item label="Total Product Quantity (Batch)" required>
            <InputNumber
              min={0}
              style={{ width: 250 }}
              value={productQuantity}
              onChange={setProductQuantity}
            />
          </Form.Item>
          <Form.Item label="Product Unit Of Measurement" required>
            <Input
              style={{ width: 250 }}
              placeholder="Enter UOM"
              value={productUOM}
              onChange={(e) => setProductUOM(e.target.value)}
            />
          </Form.Item>
        </Form>

        <StyledAntdTable
          columns={columns}
          dataSource={product.inventories || []}
          rowKey="id"
          pagination={false}
          scroll={{ y: 400 }}
        />

        <div style={{ marginTop: 20, textAlign: "right" }}>
          <StyledButton onClick={resetModalState} style={{ marginRight: 8 }}>
            Cancel
          </StyledButton>

          <StyledButton
            type="primary"
            loading={isPending}
            onClick={handleSubmit}
          >
            Submit Requirement
          </StyledButton>
        </div>
      </StyledAntdModal>

      <StyledAntdModal
        open={isInventoryModalVisible}
        onCancel={() => setIsInventoryModalVisible(false)}
        {...selectProductsModalProps}
        title={product?.productName}
      >
        <SelectProductsModal
          selectedProduct={product}
          onSuccessCallback={() => {
            setIsInventoryModalVisible(false);
            queryClient.invalidateQueries({ queryKey: ["product", id] });
          }}
        />
      </StyledAntdModal>
    </Container>
  );
};

export default ProductDetails;
